import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBarsFilter,
  faBoxArchive,
  faClockRotateLeft,
  faFileArrowDown,
  faTableLayout,
  faTrashCan,
} from "@fortawesome/pro-solid-svg-icons";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  PageTabs,
  useBatchSelection,
  withFailureReasons,
  type BatchAction,
} from "@components/CatalogIndex";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import DuplicateProjectModal from "@components/DuplicateProjectModal/DuplicateProjectModal";
import {
  useProjectArchiveActions,
  type ArchiveRunResult,
} from "./archive/useProjectArchiveActions";
import {
  archiveConfirmCopy,
  archiveFailureHeadline,
  archiveSuccessText,
  type ArchiveAction,
} from "./archive/archiveCopy";
import ProjectRowContextMenu, {
  projectMenuEntries,
  type MenuTarget,
} from "./contextMenu/ProjectRowContextMenu";
import ExportProjectsModal from "./export/ExportProjectsModal";
import { useProjectsUserView } from "./api/useProjectsUserView";
import { useProjectsListQuery } from "./api/useProjectsListQuery";
import { useFilterOptions } from "./filters/useFilterOptions";
import { activeFilterCount, filterSummary } from "./filters/filterSummary";
import FilterSlideout from "./filters/FilterSlideout";
import CustomizeTableSlideout from "./columns/CustomizeTableSlideout";
import {
  buildColumns,
  nextSortColumns,
  sortFromColumns,
} from "./columns/columnRegistry";
import {
  PROJECT_TABS,
  tabFromSavedStatus,
  type ProjectRow,
  type ProjectsTab,
} from "./types";
import "./ProjectsListV2.css";

const appHost = process.env.REACT_APP_DOORKEEPER_APP_URL ?? "";

const projectHref = (id: string) => `${appHost}/projects/${id}/edit`;

const TAB_LABELS = {
  active: "Active",
  pending: "Pending",
  archived: "Archived",
} as const;

const ProjectsListV2 = () => {
  const view = useProjectsUserView();
  const options = useFilterOptions();
  const navigate = useNavigate();
  const { hasPrivilege } = usePrivilegeCheck();
  const canCreate = hasPrivilege("projects.overview", [
    "create",
    "manage",
    "purge",
  ]);

  const [tabOverride, setTabOverride] = useState<ProjectsTab | null>(null);
  const tab = tabOverride ?? tabFromSavedStatus(view.filters.status);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [panel, setPanel] = useState<"filters" | "columns" | null>(null);
  const [showExport, setShowExport] = useState(false);

  const sort = useMemo(() => sortFromColumns(view.columns), [view.columns]);
  const params = useMemo(
    () => ({
      tab,
      search,
      page,
      pageSize: view.pageSize,
      filters: view.filters,
      sort,
    }),
    [tab, search, page, view.pageSize, view.filters, sort]
  );
  const list = useProjectsListQuery(params, { skip: view.isLoading });

  const columns = useMemo(
    () =>
      buildColumns(view.columns, {
        statusLabel: options.statusLabel,
        projectHref,
        sort,
        onSort: (column) => {
          setPage(1);
          void view.saveView({
            columns: nextSortColumns(view.columns, column),
          });
        },
      }),
    [view, options.statusLabel, sort]
  );

  const tabLabel = (key: ProjectsTab) =>
    key === "active" || key === "pending" || key === "archived"
      ? TAB_LABELS[key]
      : options.statusLabel(key);

  const filterCount = activeFilterCount(view.filters);
  const summary = filterSummary(view.filters, options);

  const resetKey = `${tab}|${search}|${page}|${JSON.stringify(view.filters)}`;

  const myProjectsToggle = (
    <Form.Check
      type="checkbox"
      id="projects-my-projects"
      label="Show My Projects Only"
      checked={!!view.filters.showMyProjects}
      onChange={(e) => {
        setPage(1);
        void view.saveView({
          filters: { ...view.filters, showMyProjects: e.target.checked },
        });
      }}
    />
  );

  const tableTools = (
    <div className="projectsListV2Tools">
      <Button
        variant="link"
        aria-label="Filter projects"
        className={filterCount > 0 ? "filterIconActive" : undefined}
        onClick={() => setPanel("filters")}
      >
        <FontAwesomeIcon icon={faBarsFilter} />
        {filterCount > 0 && (
          <span className="filterActiveCount">{filterCount} Active</span>
        )}
      </Button>
      <Button
        variant="link"
        aria-label="Customize table"
        onClick={() => setPanel("columns")}
      >
        <FontAwesomeIcon icon={faTableLayout} />
      </Button>
      <Button
        variant="link"
        aria-label="Export projects to CSV"
        disabled={view.isLoading}
        onClick={() => setShowExport(true)}
      >
        <FontAwesomeIcon icon={faFileArrowDown} />
      </Button>
    </div>
  );

  const canArchive = hasPrivilege("projects.overview", ["manage", "purge"]);
  const canPurge = hasPrivilege("projects.overview", ["purge"]);
  const canDuplicate = hasPrivilege("projects.duplicate", ["manage"]);
  const isArchivedTab = tab === "archived";

  // Selection never outlives the rows it was made on.
  // Re-sorting reorders the rows under it, so sort resets it too.
  const { selectedIds, setSelectedIds, clear } = useBatchSelection([
    resetKey,
    view.pageSize,
    JSON.stringify(sort),
  ]);
  // ids are captured when the action is chosen: the selection for a batch,
  // a single row from the context menu.
  const [pending, setPending] = useState<{
    action: ArchiveAction;
    ids: string[];
  } | null>(null);
  const [menu, setMenu] = useState<MenuTarget | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  // The row under the menu may be gone after a tab, search or page change.
  useEffect(closeMenu, [resetKey, closeMenu]);
  const [duplicateId, setDuplicateId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    variant: "success" | "error";
    text: string;
  } | null>(null);
  const archive = useProjectArchiveActions();

  const startBatch = (action: ArchiveAction) =>
    setPending({ action, ids: [...selectedIds] });

  const batchActions: BatchAction[] = isArchivedTab
    ? [
        ...(canArchive
          ? [
              {
                icon: faClockRotateLeft,
                label: "Unarchive",
                onClick: () => startBatch("restore"),
              },
            ]
          : []),
        ...(canPurge
          ? [
              {
                icon: faTrashCan,
                label: "Purge",
                variant: "danger" as const,
                onClick: () => startBatch("purge"),
              },
            ]
          : []),
      ]
    : canArchive
    ? [
        {
          icon: faBoxArchive,
          label: "Archive",
          onClick: () => startBatch("archive"),
        },
      ]
    : [];

  // setPending(null) doesn't change the pending action a second click in
  // the same render sees, so a ref guards against running the batch twice.
  const running = useRef(false);

  const pendingRows = pending
    ? list.rows.filter((r) => pending.ids.includes(r.id))
    : [];

  const handleConfirm = async () => {
    if (!pending || running.current) return;
    running.current = true;
    const { action, ids } = pending;
    const names = new Map(
      pendingRows.map((r) => [
        r.id,
        String(r.project.attributes?.["project-name"] ?? ""),
      ])
    );
    setPending(null);
    let result: ArchiveRunResult;
    try {
      result = await archive.run(action, ids);
    } finally {
      running.current = false;
    }
    const { succeeded, failures } = result;
    clear();
    setToast(
      failures.length === 0
        ? {
            variant: "success",
            text: archiveSuccessText(action, succeeded.length),
          }
        : {
            variant: "error",
            text: withFailureReasons(
              archiveFailureHeadline(action, failures.length, ids.length),
              // withFailureReasons drops entries without a reason; a named
              // project still gets listed so the user knows which one failed.
              failures.map((f) => {
                const label = names.get(f.id) || undefined;
                return { label, reason: f.reason ?? (label ? "failed" : null) };
              })
            ),
          }
    );
    list.refetch();
  };

  const confirmCopy = pending
    ? archiveConfirmCopy(
        pending.action,
        pending.ids.length,
        pendingRows.reduce((n, r) => n + r.changeOrders.length, 0)
      )
    : null;

  const tabs = (
    <div className="projectsListV2Controls">
      <PageTabs
        activeKey={tab}
        onSelect={(k) => {
          setTabOverride(k as ProjectsTab);
          setPage(1);
        }}
        tabs={PROJECT_TABS.map((t) => ({
          key: t.key,
          label: tabLabel(t.key),
          count: t.key === tab && !list.isLoading ? list.totalRows : null,
        }))}
      />
    </div>
  );

  return (
    <>
      {view.loadFailed && (
        <Alert variant="warning" dismissible>
          Your table settings couldn't be loaded. Changes won't be saved this
          visit.
        </Alert>
      )}
      {view.saveError && (
        <ToastAlert
          onClose={view.clearSaveError}
          variant="danger"
          text="Couldn't save your table settings."
        />
      )}
      <AdminListCard
        title="Projects"
        onNew={canCreate ? () => navigate("/projects/new") : undefined}
        newButtonLabel="Create New Project"
        selectedCount={selectedIds.length}
        batchActions={batchActions}
        searchProps={{
          label: "Search Projects",
          labelAccessory: myProjectsToggle,
          trailing: tableTools,
          placeholder: "Search by project name",
          value: searchInput,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchInput(e.target.value),
          onSubmit: (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            setSearch(searchInput);
            setPage(1);
          },
          onClear: () => {
            setSearchInput("");
            setSearch("");
            setPage(1);
          },
        }}
      >
        {summary.length > 0 && (
          <p className="projectsFilterSummary">
            Filtering By:{" "}
            {summary.map((item, i) => (
              <span key={item.label}>
                <strong>{item.label}</strong> ({item.value})
                {i < summary.length - 1 ? ", " : ""}
              </span>
            ))}
          </p>
        )}
        {tabs}
        {list.isError ? (
          <div className="projectsListV2Error px-3 py-4">
            <p>Couldn't load projects.</p>
            <Button variant="outline-secondary" onClick={() => list.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <AdminListTable<ProjectRow>
            columns={columns}
            data={list.rows}
            isLoading={view.isLoading || list.isLoading}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            selectable={batchActions.length > 0}
            canSelectRow={(row) => row.depth === 0}
            emptyMessage="No projects found."
            paginationEnabled
            pageSizeEnabled
            totalPages={list.totalPages}
            totalRows={list.totalRows}
            currentPage={page}
            setCurrentPage={setPage}
            pageSize={view.pageSize}
            onPageSizeChange={(size) => {
              setPage(1);
              void view.saveView({ pageSize: size });
            }}
            getSubRows={(r) =>
              r.changeOrders.length ? r.changeOrders : undefined
            }
            getRowClassName={(row) =>
              [
                row.depth > 0
                  ? "changeOrderChildRow"
                  : row.original.changeOrders.length
                  ? "changeOrderParentRow"
                  : null,
                menu?.row.id === row.original.id ? "contextMenuOpenRow" : null,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
            onRowContextMenu={(row, e) => {
              e.preventDefault();
              setMenu({
                row: row.original,
                isChild: row.depth > 0,
                x: e.pageX,
                y: e.pageY,
              });
            }}
            subRowsToggleLabel={(_r, n, open) =>
              `${open ? "Hide" : "Show"} ${n} change ${
                n === 1 ? "request" : "requests"
              }`
            }
            resetExpandedKey={resetKey}
          />
        )}
      </AdminListCard>
      <FilterSlideout
        show={panel === "filters"}
        onClose={() => setPanel(null)}
        filters={view.filters}
        options={options}
        onApply={(next) => {
          setPage(1);
          void view.saveView({ filters: next });
        }}
      />
      <CustomizeTableSlideout
        show={panel === "columns"}
        onClose={() => setPanel(null)}
        columns={view.columns}
        onApply={(next) => {
          setPage(1);
          void view.saveView({ columns: next });
        }}
        onReset={() => {
          setPage(1);
          void view.resetView();
        }}
      />
      <ConfirmDeleteModal
        open={!!confirmCopy}
        title={confirmCopy?.title ?? ""}
        message={confirmCopy?.message ?? ""}
        confirmLabel={confirmCopy?.confirmLabel}
        onConfirm={() => void handleConfirm()}
        onCancel={() => setPending(null)}
      />
      {menu && (
        <ProjectRowContextMenu
          target={menu}
          entries={projectMenuEntries(menu, {
            href: projectHref(menu.row.id),
            isArchivedTab,
            canArchive,
            canPurge,
            canDuplicate,
          })}
          onAction={(action, row) =>
            action === "duplicate"
              ? setDuplicateId(row.id)
              : setPending({ action, ids: [row.id] })
          }
          onClose={closeMenu}
        />
      )}
      {duplicateId && (
        <DuplicateProjectModal
          projectId={duplicateId}
          show
          onClose={() => setDuplicateId(null)}
          // The copy is made by a background job, so there is nothing to
          // refetch yet; the message says it will show up shortly.
          onSuccess={(text) => setToast({ variant: "success", text })}
        />
      )}
      <ExportProjectsModal
        show={showExport}
        params={params}
        onClose={() => setShowExport(false)}
        onResult={setToast}
      />
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default ProjectsListV2;
